
import React from "react";
import SimpleImageRenderer from "./SimpleImageRenderer";
import MarkdownRenderer from "./MarkdownRenderer";
import { cleanAIResponse, processContentForLinks } from "../../utils/textCleaningUtils";
import { isEnhancedFeedbackAnalysis } from "../../utils/enhancedFeedbackDetection";
import { ProcessedFile } from "../../context/types";

interface FileContentRendererProps {
  content: string;
  type: "system" | "user";
  files: ProcessedFile[];
}

const FileContentRenderer: React.FC<FileContentRendererProps> = ({ content, type, files }) => {
  console.log('🎯 CHATMESSAGE-RENDER: PRIORITY PIPELINE - Processing attached files');
  
  // Extract images from files prop - handle both URLs and base64
  const validImageData: string[] = [];
  files.forEach((file, index) => {
    console.log(`🎯 CHATMESSAGE-RENDER: Checking file ${index + 1}:`, {
      type: file.type,
      filename: file.filename,
      hasContent: !!file.content,
      isValidUrl: file.content?.startsWith('http'),
      isValidDataUrl: file.content?.startsWith('data:image/'),
      contentLength: file.content?.length || 0,
      contentPrefix: file.content?.substring(0, 50)
    });
    
    if (file.type === 'image' && file.content) {
      // ENHANCED: Handle both URL and base64 images with validation
      if (file.content.startsWith('http') || file.content.startsWith('data:image/')) {
        validImageData.push(file.content);
        console.log(`✅ CHATMESSAGE-RENDER: Added valid image ${index + 1} to display list`);
      } else {
        console.log(`❌ CHATMESSAGE-RENDER: Rejected invalid image ${index + 1}:`, {
          contentType: typeof file.content,
          hasContent: !!file.content,
          contentPreview: file.content.substring(0, 100)
        });
      }
    }
  });
  
  // If we have valid images from files, render them with enhanced SimpleImageRenderer
  if (validImageData.length > 0) {
    console.log('🎯 CHATMESSAGE-RENDER: RENDERING', validImageData.length, 'images from files prop');
    console.log('🎯 CHATMESSAGE-RENDER: Image URLs to render:', validImageData.map(url => ({
      isUrl: url.startsWith('http'),
      isDataUrl: url.startsWith('data:image/'),
      urlPrefix: url.substring(0, 100)
    })));
    
    // Process content for text display using enhanced feedback detection
    const processedContent = (() => {
      if (type === "system" && isEnhancedFeedbackAnalysis(content)) {
        console.log('🎯 CHATMESSAGE-RENDER: Enhanced feedback analysis - preserving formatting');
        return content; // No processing for feedback content
      }
      
      const cleanedContent = type === "system" ? cleanAIResponse(content) : content;
      return processContentForLinks(cleanedContent);
    })();

    console.log('🎯 CHATMESSAGE-RENDER: Processed content for display:', {
      hasProcessedContent: !!processedContent,
      contentLength: processedContent?.length || 0
    });

    return (
      <>
        <SimpleImageRenderer images={validImageData} />
        {processedContent && (
          <MarkdownRenderer content={processedContent} type={type} />
        )}
      </>
    );
  } else {
    console.log('❌ CHATMESSAGE-RENDER: No valid images found in files prop');
    return null;
  }
};

export default FileContentRenderer;
